import { Component, EventEmitter, HostBinding, HostListener, Input, OnDestroy, Output } from '@angular/core';
import { Subscription } from 'rxjs';
import { DataService } from 'src/app/services/data.service';

/**
 * The drawer's shell: header, optional accent dot + subtitle, close button and
 * the rise-in animation. Controls project their own content into it, so every
 * toolbar panel looks and behaves the same no matter which control opened it.
 *
 * Styles live HERE rather than in the toolbar host's `::ng-deep` block, because
 * the drawer is rendered through a CDK overlay attached to the document body —
 * outside `.tb-host` entirely — so host-scoped selectors would never match.
 *
 * The dark class is read from `DataService` for the same reason the toolbar
 * reads it: a drawer must never come up on a different ground from the bar that
 * opened it, and the two now derive that ground from one source rather than from
 * two different positions in the DOM.
 */
@Component({
  selector: 'app-toolbar-drawer',
  template: `
    <div class="tb-drawer" [class.tb-drawer--dark]="dark">
      <div class="tb-drawer__head">
        <span class="tb-drawer__dot" *ngIf="accent" [style.background]="accent"></span>
        <span class="tb-drawer__title">{{ title }}</span>
        <span class="tb-drawer__subtitle" *ngIf="subtitle">{{ subtitle }}</span>
        <button type="button" class="tb-drawer__action" *ngIf="actionLabel"
                [disabled]="actionDisabled" (click)="action.emit()">
          {{ actionLabel }}
        </button>
        <button type="button" class="tb-drawer__close" (click)="close.emit()" aria-label="Close">
          <mat-icon>close</mat-icon>
        </button>
      </div>
      <div class="tb-drawer__body">
        <ng-content></ng-content>
      </div>
    </div>`,
  styleUrls: ['./toolbar-drawer.component.scss'],
})
export class ToolbarDrawerComponent implements OnDestroy {
  /**
   * `title` is an @Input AND a global HTML attribute, and a STATIC one in a
   * template (`<app-toolbar-drawer title="Port calls">`) is written to the DOM
   * as well as bound to the input. The browser then draws its own native
   * tooltip from it over the whole drawer — so hovering a field's info icon
   * showed two tooltips at once, the Material one and a bare "Port calls".
   *
   * Cleared here rather than by rewriting every call site to `[title]="'…'"`:
   * seven of them across three providers had the static form, and the next one
   * written would have had it too. A host binding of null removes the
   * attribute after the static one is applied, so both spellings are safe.
   */
  @HostBinding('attr.title') readonly hostTitle: null = null;

  @Input() title = '';
  // `| undefined` matters: callers bind optional PlaybackState fields straight
  // in, and AOT template type-checking rejects a narrower input type.
  @Input() subtitle: string | null | undefined = null;
  /** Optional colour dot (e.g. the traced vessel's own colour). */
  @Input() accent: string | null | undefined = null;
  /**
   * Optional header command, shown left of the ✕ — e.g. "Clear" on the vessel
   * type filter. Any panel can opt in; omit the label and no button renders.
   */
  @Input() actionLabel: string | null = null;
  @Input() actionDisabled = false;
  @Output() action = new EventEmitter<void>();
  @Output() close = new EventEmitter<void>();

  dark = false;
  private _themeSub?: Subscription;

  constructor(private data: DataService) {
    this._themeSub = this.data.darkTheme.subscribe((v) => (this.dark = !!v));
  }

  @HostListener('click', ['$event'])
  @HostListener('mousedown', ['$event'])
  @HostListener('dblclick', ['$event'])
  @HostListener('pointerdown', ['$event'])
  onInteract(event: Event): void {
    event.stopPropagation();
  }

  ngOnDestroy(): void {
    this._themeSub?.unsubscribe();
  }
}
